import React from 'react';
import { Breadcrumbs, Link, Typography, BreadcrumbsProps } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { useAppNavigation } from '../../hooks/useAppNavigation';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export interface AppBreadcrumbsProps extends Omit<BreadcrumbsProps, 'children'> {
  items: BreadcrumbItem[];
}

/**
 * AppBreadcrumbs is a standardized breadcrumb component that uses
 * programmatic navigation via the useAppNavigation hook. This ensures
 * consistent navigation behavior across all breadcrumb links.
 */
export const AppBreadcrumbs: React.FC<AppBreadcrumbsProps> = ({
  items,
  ...breadcrumbsProps
}) => {
  const { navigateTo } = useAppNavigation();

  const handleClick = (path: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    navigateTo(path);
  };

  return (
    <Breadcrumbs
      separator={<NavigateNext fontSize="small" />}
      aria-label="breadcrumb"
      {...breadcrumbsProps}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        if (isLast || !item.path) {
          return (
            <Typography key={index} color="text.primary">
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            color="inherit"
            href={item.path}
            onClick={handleClick(item.path)}
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            {item.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};